import {
  getCustomTypesOptions,
  getTeamSpacesOptions,
  type TeamSpace,
} from "@prismicio/editor-fields"
import { usePrefetchQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import { env } from "../env"
import { getRepositoryName } from "../routing"

export function useTeamSpaces() {
  const { data: teamSpaces } = useSuspenseQuery(
    getTeamSpacesOptions({
      baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
      repository: getRepositoryName(),
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    }),
  )

  const { data: customTypes } = useSuspenseQuery(
    getCustomTypesOptions({
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      baseUrl: env.PRISMIC_CUSTOM_TYPES_API_BASE_URL,
      repository: getRepositoryName(),
    }),
  )

  return useMemo(() => {
    const assignedCustomTypes = teamSpaces?.flatMap(
      (teamSpace) => teamSpace.customTypes,
    )

    // Main Space consists of all enabled custom types
    // (customType.status is a boolean field, true means enabled)
    // that are not assigned to any Team Space
    const mainSpaceCustomTypes = customTypes
      ?.filter(
        (customType) =>
          customType.status && !assignedCustomTypes?.includes(customType.id),
      )
      .map((customType) => customType.id)

    const sortedTeamSpaces = (teamSpaces ?? []).sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    return [
      {
        ...mainSpace,
        customTypes: mainSpaceCustomTypes,
      },
      ...sortedTeamSpaces,
    ]
  }, [teamSpaces, customTypes])
}

export function useTeamSpace(teamSpaceId: string = mainSpace.id) {
  const teamSpaces = useTeamSpaces()

  return teamSpaces.find((teamSpace) => teamSpace.id === teamSpaceId)
}

export const mainSpace: TeamSpace = {
  id: "",
  name: "Main Space",
  customTypes: [],
}

export function usePrefetchTeamSpacesQuery() {
  usePrefetchQuery(
    getTeamSpacesOptions({
      baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
      repository: getRepositoryName(),
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    }),
  )
}
